/**
 * Broadcast for events as well as churches (site-admin API v0.19): audience
 * presets, the Theme › Texts table, sponsor / links / backup stream limits and
 * the register-to-watch field rules. Mirrors ott-cms
 * `lib/sites/broadcast-events.ts` (and `messages/en.json`) — keep in sync.
 *
 * Unset `broadcastAudience` reads `church` on the runtime so sites created
 * before v0.19 keep their wording; new sites are created with `general`.
 */

export const AUDIENCE_CHURCH = 'church'
export const AUDIENCE_EVENT = 'event'
export const AUDIENCE_GENERAL = 'general'
export const AUDIENCE_DEFAULT_NEW = AUDIENCE_GENERAL

export const AUDIENCE_PRESETS = [
	{ id: AUDIENCE_EVENT, title: 'Event or conference', description: 'Sessions, an agenda and “Donate” wording. Pairs well with a registration form.' },
	{ id: AUDIENCE_CHURCH, title: 'Church or ministry', description: 'Services, service times and a Give button.' },
	{ id: AUDIENCE_GENERAL, title: 'General', description: 'Neutral wording: streams, a schedule and “Donate”.' },
]

/** Runtime rule: unset / unknown → church. */
export function normalizeAudience(value) {
	return AUDIENCE_PRESETS.some((p) => p.id === value) ? value : AUDIENCE_CHURCH
}

/** Dashboard labels of Settings › Broadcast that follow the preset. */
const DASHBOARD_LABELS = {
	church: { infoTitle: 'Service info', infoDescription: 'What viewers see around the player: your tagline, service times, About and where to find you.', times: 'Service times', timesHint: 'Service times are read in this zone; viewers see them converted to theirs.', row: 'service time', rowExample: 'Sunday Service', recent: 'Recent services row', recentHint: 'Optional — by default all recordings show. A category keeps sermons apart from other videos.', liveLabel: 'Service stream', pageDescription: 'Service times, About, chat and giving on your home page.', tzSuggested: 'change it if the services happen elsewhere', rowTitle: 'Service time', add: 'Add service time', placeholder: 'e.g. Sunday service', empty: 'No service times yet. They drive the “Next service” countdown while you are offline and the Service times block on the home page.', maxNote: 'service times' },
	event: { infoTitle: 'Event info', infoDescription: 'What viewers see around the player: your tagline, agenda, About and where to find you.', times: 'Agenda', timesHint: 'Session times are read in this zone; viewers see them converted to theirs.', row: 'session', rowExample: 'Keynote', recent: 'Past sessions row', recentHint: 'Optional — by default all recordings show. A category keeps session recordings apart from other videos.', liveLabel: 'Event stream', pageDescription: 'Agenda, About, sponsor, links, chat and donations on your home page.', tzSuggested: 'change it if the event happens elsewhere', rowTitle: 'Session', add: 'Add session', placeholder: 'e.g. Opening keynote', empty: 'No sessions yet. They drive the “Next session” countdown while you are offline and the Agenda block on the home page.', maxNote: 'sessions' },
	general: { infoTitle: 'Stream info', infoDescription: 'What viewers see around the player: your tagline, schedule, About and where to find you.', times: 'Schedule', timesHint: 'Times are read in this zone; viewers see them converted to theirs.', row: 'stream time', rowExample: 'Weekly show', recent: 'Recent streams row', recentHint: 'Optional — by default all recordings show. A category keeps some recordings apart from other videos.', liveLabel: 'Live stream', pageDescription: 'Schedule, About, chat and donations on your home page.', tzSuggested: 'change it if you stream from elsewhere', rowTitle: 'Stream time', add: 'Add stream time', placeholder: 'e.g. Weekly show', empty: 'No stream times yet. They drive the “Next stream” countdown while you are offline and the Schedule block on the home page.', maxNote: 'stream times' },
}
export function audienceLabels(audience) {
	return DASHBOARD_LABELS[normalizeAudience(audience)]
}

// --- Theme › Texts (`broadcastTexts`) --------------------------------------------

export const TEXT_GROUPS = [
	{ id: 'page', title: 'Page and player', description: 'Status badges, the countdown, the schedule and the About block.' },
	{ id: 'register', title: 'Registration form', description: 'The register-to-watch panel shown in place of the player.' },
	{ id: 'giving', title: 'Giving', description: 'The Give / Donate button and the donation sheet.' },
	{ id: 'extras', title: 'Sponsor and links', description: 'The sponsor label in the header and the backup stream link.' },
]

/** Every overridable string: flat key → shipped wording per preset (church is the base). */
export const BROADCAST_TEXT_FIELDS = [
	{ key: 'live', where: 'Status badge', group: 'page', defaults: { church: "Live" } },
	{ key: 'offline', where: 'Status badge', group: 'page', defaults: { church: "Offline" } },
	{ key: 'upcoming', where: 'Status badge', group: 'page', defaults: { church: "Upcoming" } },
	{ key: 'nextService', where: 'Status line · next scheduled time', group: 'page', defaults: { church: "Next service", event: "Next session", general: "Next stream" } },
	{ key: 'nextBroadcast', where: 'Status line · next one-off stream', group: 'page', defaults: { church: "Next broadcast" } },
	{ key: 'serviceNow', where: 'Status line · scheduled time on now', group: 'page', defaults: { church: "Service", event: "Session", general: "Live now" } },
	{ key: 'broadcastNow', where: 'Status line · one-off stream on now', group: 'page', defaults: { church: "Broadcast" } },
	{ key: 'startsIn', where: 'Countdown · scheduled time', group: 'page', defaults: { church: "Next service starts in", event: "Next session starts in", general: "Next stream starts in" } },
	{ key: 'broadcastStartsIn', where: 'Countdown · one-off stream', group: 'page', defaults: { church: "Next broadcast starts in" } },
	{ key: 'specialStartsIn', where: 'Countdown · special event', group: 'page', defaults: { church: "Special event starts in" } },
	{ key: 'specialBroadcast', where: 'Status line · another one-off coming up', group: 'page', defaults: { church: "Special broadcast" } },
	{ key: 'upcomingService', where: 'Upcoming list badge · scheduled time', group: 'page', defaults: { church: "Service", event: "Session", general: "Stream" } },
	{ key: 'upcomingSpecial', where: 'Upcoming list badge · special event', group: 'page', defaults: { church: "Special event" } },
	{ key: 'upcomingBroadcast', where: 'Upcoming list badge · one-off stream', group: 'page', defaults: { church: "Broadcast" } },
	{ key: 'offlineNow', where: 'Stage · nothing scheduled', group: 'page', defaults: { church: "We are offline right now" } },
	{ key: 'watchLastService', group: 'page', defaults: { church: "Watch last service", event: "Watch last session", general: "Watch last stream" } },
	{ key: 'watchLiveAnyway', group: 'page', defaults: { church: "Watch live anyway" } },
	{ key: 'lastService', group: 'page', defaults: { church: "Last service", event: "Last session", general: "Last stream" } },
	{ key: 'noLiveHint', group: 'page', defaults: { church: "The live stream will appear here during the next service.", event: "The live stream will appear here when the next session starts.", general: "The live stream will appear here when the next stream starts." } },
	{ key: 'share', group: 'page', defaults: { church: "Share" } },
	{ key: 'linkCopied', group: 'page', defaults: { church: "Link copied" } },
	{ key: 'about', group: 'page', defaults: { church: "About" } },
	{ key: 'directions', group: 'page', defaults: { church: "Directions" } },
	{ key: 'readMore', group: 'page', defaults: { church: "Read more" } },
	{ key: 'readLess', group: 'page', defaults: { church: "Read less" } },
	{ key: 'serviceTimes', group: 'page', defaults: { church: "Service times", event: "Agenda", general: "Schedule" } },
	{ key: 'giving', group: 'giving', defaults: { church: "Giving", event: "Support", general: "Support" } },
	{ key: 'giveOnline', group: 'giving', defaults: { church: "Give online", event: "Donate online", general: "Donate online" } },
	{ key: 'recentServices', group: 'page', defaults: { church: "Recent services", event: "Past sessions", general: "Recent streams" } },
	{ key: 'upcomingHeading', group: 'page', defaults: { church: "Upcoming" } },
	{ key: 'signInToWatch', where: 'Button on a locked player', group: 'page', defaults: { church: "Sign in to watch" } },
	{ key: 'unlockToWatch', where: 'Paid item', group: 'page', defaults: { church: "Unlock to watch" } },
	{ key: 'verifyEmailToWatch', group: 'page', defaults: { church: "Verify your email to watch" } },
	{ key: 'sponsorLabel', where: 'Header, next to the sponsor logo', group: 'extras', defaults: { church: "Stream supported by" } },
	{ key: 'backupStream', where: 'Link under the player', group: 'extras', defaults: { church: "Backup stream" } },
	{ key: 'gateTitle', group: 'register', defaults: { church: "Register to watch" } },
	{ key: 'gateSubtitle', group: 'register', defaults: { church: "Enter your details to watch the live stream." } },
	{ key: 'gateName', group: 'register', defaults: { church: "Full name" } },
	{ key: 'gateFirstName', group: 'register', defaults: { church: "First name" } },
	{ key: 'gateLastName', group: 'register', defaults: { church: "Last name" } },
	{ key: 'gateEmail', group: 'register', defaults: { church: "Email" } },
	{ key: 'gateSubmit', group: 'register', defaults: { church: "Register and watch" } },
	{ key: 'gateHaveAccount', group: 'register', defaults: { church: "Already registered?" } },
	{ key: 'gateSignIn', where: 'Link under the form', group: 'register', defaults: { church: "Sign in" } },
	{ key: 'gateSignInTitle', where: 'Sign-in panel title', group: 'register', defaults: { church: "Sign in to watch" } },
	{ key: 'gateSignInSubtitle', group: 'register', defaults: { church: "We will email you a 6-digit sign-in code." } },
	{ key: 'gateSendCode', group: 'register', defaults: { church: "Email me a sign-in code" } },
	{ key: 'gateCodeLabel', group: 'register', defaults: { church: "6-digit code" } },
	{ key: 'gateVerify', group: 'register', defaults: { church: "Continue" } },
	{ key: 'gateNewHere', group: 'register', defaults: { church: "New here?" } },
	{ key: 'gateRegisterLink', group: 'register', defaults: { church: "Register" } },
	{ key: 'gateUsePassword', group: 'register', defaults: { church: "Use your password instead" } },
	{ key: 'gateRegistered', group: 'register', defaults: { church: "You are registered. The stream will appear here when it starts." } },
	{ key: 'giveSheetTitle', group: 'giving', defaults: { church: "Give to {name}", event: "Support {name}", general: "Support {name}" } },
	{ key: 'giveSheetAmount', group: 'giving', defaults: { church: "Amount" } },
	{ key: 'giveSheetOtherAmount', group: 'giving', defaults: { church: "Other amount" } },
	{ key: 'giveSheetEmail', group: 'giving', defaults: { church: "Email" } },
	{ key: 'giveSheetEmailHint', group: 'giving', defaults: { church: "We'll email your receipt to this address." } },
	{ key: 'giveSheetContinue', group: 'giving', defaults: { church: "Continue" } },
	{ key: 'giveSheetGiveAmount', group: 'giving', defaults: { church: "Give {amount}", event: "Donate {amount}", general: "Donate {amount}" } },
	{ key: 'giveSheetSecure', group: 'giving', defaults: { church: "Secure payment by Stripe · receipt by email" } },
	{ key: 'giveSheetUnavailable', group: 'giving', defaults: { church: "Online giving is temporarily unavailable.", event: "Online donations are temporarily unavailable.", general: "Online donations are temporarily unavailable." } },
	{ key: 'giveSheetThankYou', group: 'giving', defaults: { church: "Thank you" } },
	{ key: 'giveSheetBackToService', group: 'giving', defaults: { church: "Back to service", event: "Back to the stream", general: "Back to the stream" } },
	{ key: 'giveSheetGiveAgain', group: 'giving', defaults: { church: "Give again", event: "Donate again", general: "Donate again" } },
]
export const BROADCAST_TEXT_MAX = 200

/** The wording viewers see for a key when the owner has not overridden it. */
export function defaultText(field, audience) {
	const a = normalizeAudience(audience)
	return field.defaults[a] ?? field.defaults.church
}

/** `broadcastTexts` → known keys with a trimmed, single-line, non-empty value. */
export function normalizeTexts(value) {
	const out = {}
	if (!value || typeof value !== 'object' || Array.isArray(value)) return out
	for (const f of BROADCAST_TEXT_FIELDS) {
		const v = value[f.key]
		if (typeof v !== 'string') continue
		// eslint-disable-next-line no-control-regex
		const text = v.replace(/[\u0000-\u001F\u007F]/g, ' ').trim()
		if (text) out[f.key] = text
	}
	return out
}

// --- Sponsor, links, backup stream ------------------------------------------------

export const LINKS_MAX = 5
export const LINK_LABEL_MAX = 40
export const URL_MAX = 2048

/** Link row URL: https:// or a site path (`/agenda`). */
export function linkUrlError(url) {
	const v = String(url || '').trim()
	if (!v) return 'Add a URL'
	if (v.length > URL_MAX) return 'URL is too long'
	if (v.startsWith('/')) return v.startsWith('//') ? 'Use https:// or a path like /agenda' : null
	if (!/^https:\/\/[^\s/]+\.[^\s]+$/i.test(v)) return 'Use an https:// address or a site path like /agenda'
	return null
}

export function backupUrlError(url) {
	const v = String(url || '').trim()
	if (!v) return null
	if (v.length > URL_MAX) return 'URL is too long'
	return /^https:\/\/[^\s/]+\.[^\s]+$/i.test(v) ? null : 'Use an https:// address'
}

// --- Register to watch -------------------------------------------------------------

export const REGISTRATION_FIELD_TYPES = [
	{ value: 'text', text: 'Text' },
	{ value: 'email', text: 'Email' },
	{ value: 'checkbox', text: 'Checkbox' },
	{ value: 'select', text: 'Dropdown' },
]
export const REGISTRATION_FIELDS_MAX = 8
export const REGISTRATION_FIELD_LABEL_MAX = 120
export const REGISTRATION_OPTIONS_MAX = 20
export const REGISTRATION_OPTION_MAX = 80
export const REGISTRATION_CONSENT_TEXT_MAX = 300
export const RESERVED_FIELD_KEYS = ['name', 'first_name', 'last_name', 'email', 'consent', 'code', 'password']
const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/

export const NAME_FORMATS = [
	{ value: 'full', text: 'One “Full name” field' },
	{ value: 'split', text: 'First and last name' },
]

/** A stable field key from its label ("Company name" → `company_name`), unique among `taken`. */
export function fieldKeyFor(label, taken = []) {
	let base = String(label || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
	if (!/^[a-z]/.test(base)) base = `field_${base}`.replace(/_+$/g, '')
	base = base.slice(0, 26).replace(/_+$/g, '') || 'field'
	if (RESERVED_FIELD_KEYS.includes(base)) base = `${base}_field`
	let key = base
	for (let i = 2; taken.includes(key); i++) key = `${base}_${i}`
	return key
}

/** Runtime value → editor rows. */
export function fieldsFromSettings(value) {
	return (Array.isArray(value) ? value : []).filter((f) => f && FIELD_KEY_RE.test(String(f.key || ''))).map((f) => ({
		key: String(f.key),
		label: String(f.label || ''),
		type: REGISTRATION_FIELD_TYPES.some((t) => t.value === f.type) ? f.type : 'text',
		required: f.required === true,
		options: Array.isArray(f.options) ? f.options.map(String) : [],
	}))
}

/** Editor rows → the runtime shape (`options` only on dropdowns). */
export function fieldsToRuntime(rows) {
	return rows.map((r) => ({
		key: r.key,
		label: String(r.label || '').trim(),
		type: r.type,
		required: r.required === true,
		...(r.type === 'select' ? { options: r.options.map((o) => String(o).trim()).filter(Boolean) } : {}),
	}))
}

/** Per-row message (or null), mirroring the runtime's `parseRegistrationFields`. */
export function fieldErrors(rows) {
	const seen = new Set()
	return rows.map((r) => {
		const label = String(r.label || '').trim()
		if (!label) return 'Add a label'
		if (label.length > REGISTRATION_FIELD_LABEL_MAX) return `Label: up to ${REGISTRATION_FIELD_LABEL_MAX} characters`
		if (!FIELD_KEY_RE.test(r.key) || RESERVED_FIELD_KEYS.includes(r.key)) return 'This field name is reserved — pick another label'
		if (seen.has(r.key)) return 'Two fields share this name'
		seen.add(r.key)
		if (r.type === 'select') {
			const opts = r.options.map((o) => String(o).trim()).filter(Boolean)
			if (!opts.length) return 'Add at least one option'
			if (opts.length > REGISTRATION_OPTIONS_MAX) return `Up to ${REGISTRATION_OPTIONS_MAX} options`
			if (opts.some((o) => o.length > REGISTRATION_OPTION_MAX)) return `Options: up to ${REGISTRATION_OPTION_MAX} characters each`
			if (new Set(opts).size !== opts.length) return 'Two options are the same'
		}
		return null
	})
}

/** Consent link: https:// or a site path, like the Terms / Privacy links. */
export function consentUrlError(url) {
	const v = String(url || '').trim()
	if (!v) return null
	if (v.length > URL_MAX) return 'URL is too long'
	if (v.startsWith('/')) return /^\/[a-z0-9][a-z0-9-]*$/i.test(v) ? null : 'Use a site page path like /privacy (one segment) or a full https:// URL'
	return /^https:\/\/[^\s/]+\.[^\s]+$/i.test(v) ? null : 'Use an https:// address or a site path like /privacy'
}
